import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import type { TenantDomain, DnsRecord } from "@/types";

interface DomainInstructionsProps {
  domain: TenantDomain;
  onBack: () => void;
  onVerify: (domainId: string) => void;
  isVerifying: boolean;
}

const STEP_LABELS = [
  { label: "Add DNS Records", statuses: ["PENDING_OWNERSHIP", "PENDING_CERT"] },
  { label: "SSL Certificate", statuses: ["DEPLOYING"] },
  { label: "Domain Connected", statuses: ["PENDING_DNS"] },
  { label: "Live", statuses: ["ACTIVE"] },
];

function getActiveStep(status: string): number {
  for (let i = 0; i < STEP_LABELS.length; i++) {
    if (STEP_LABELS[i].statuses.includes(status)) return i;
  }
  if (status === "ACTIVE") return STEP_LABELS.length;
  return 0;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

function CopyableValue({ value }: { value: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="body2"
        fontFamily="monospace"
        sx={{ wordBreak: "break-all" }}
      >
        {value}
      </Typography>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => copyToClipboard(value)}>
          <CopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

interface DnsRow extends DnsRecord {
  purpose: string;
}

function DnsTable({ records }: { records: DnsRow[] }) {
  return (
    <TableContainer
      sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell sx={{ fontWeight: 600, borderRight: 1, borderColor: "divider" }}>Purpose</TableCell>
            <TableCell sx={{ fontWeight: 600, borderRight: 1, borderColor: "divider" }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600, borderRight: 1, borderColor: "divider" }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((record, index) => (
            <TableRow
              key={index}
              sx={{
                bgcolor: index % 2 === 0 ? "background.paper" : "action.hover",
              }}
            >
              <TableCell sx={{ borderRight: 1, borderColor: "divider" }}>
                <Typography variant="body2" fontWeight={500}>
                  {record.purpose}
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: 1, borderColor: "divider" }}>
                <Typography variant="body2" fontFamily="monospace">
                  {record.type}
                </Typography>
              </TableCell>
              <TableCell sx={{ borderRight: 1, borderColor: "divider" }}>
                <CopyableValue value={record.name} />
              </TableCell>
              <TableCell>
                <CopyableValue value={record.value} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const DomainInstructions: React.FC<DomainInstructionsProps> = ({
  domain,
  onBack,
  onVerify,
  isVerifying,
}) => {
  const activeStep = getActiveStep(domain.status);
  const isActive = domain.status === "ACTIVE";
  const isFailed = domain.status === "FAILED";
  const isDeploying = domain.status === "DEPLOYING";
  const needsAction =
    !isActive && domain.status !== "REMOVED" && !isDeploying;

  // Build table rows: SSL records + domain pointing
  const rows: DnsRow[] = [
    ...domain.required_dns_records.map((r) => ({
      ...r,
      purpose: "SSL Validation",
    })),
    ...(domain.cloudfront_domain
      ? [
          {
            type: "CNAME",
            name: domain.hostname,
            value: domain.cloudfront_domain,
            purpose: "Domain Pointing",
          },
        ]
      : []),
  ];

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack} aria-label="back to domains">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h6" fontFamily="monospace">
            {domain.hostname}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Custom domain setup
          </Typography>
        </Box>
      </Stack>

      {/* ── Progress Stepper ── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((step, index) => (
            <Step key={step.label}>
              <StepLabel error={isFailed && index === activeStep}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Banners */}
      {isActive && (
        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
          All steps completed — your domain is live and serving traffic.
        </Alert>
      )}
      {isFailed && domain.error_message && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => onVerify(domain.id)}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Retry"
              )}
            </Button>
          }
        >
          {domain.error_message}
        </Alert>
      )}
      {isDeploying && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 3 }}
        >
          CloudFront distribution is being created. This usually takes 2–15
          minutes.
        </Alert>
      )}

      {/* ── DNS Records Table ── */}
      {rows.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            {isActive
              ? "Configured DNS Records"
              : "Add these CNAME records to your domain"}
          </Typography>
          {!isActive && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Go to your domain registrar's DNS settings and create the
              following records:
            </Typography>
          )}

          <DnsTable records={rows} />

          {!isActive && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Tip:</strong> Some DNS providers automatically append
                your domain. If so, only enter the part before your domain (e.g.{" "}
                <code>_abc123.app</code> instead of{" "}
                <code>_abc123.app.example.com</code>).
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Verify / Check Status */}
      {needsAction && (
        <Box>
          <Button
            variant="contained"
            onClick={() => onVerify(domain.id)}
            disabled={isVerifying}
            startIcon={
              isVerifying ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Verify DNS Records
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            DNS changes can take up to 48 hours to propagate. If verification
            fails, wait a few minutes and try again.
          </Typography>
        </Box>
      )}
      {isDeploying && (
        <Button
          variant="outlined"
          onClick={() => onVerify(domain.id)}
          disabled={isVerifying}
          startIcon={
            isVerifying ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Check Status
        </Button>
      )}
    </Box>
  );
};

export default DomainInstructions;
